// Shared, dependency-free auth rules — imported by BOTH the browser
// (systems/auth.js, ui/authPanel.js) and the Worker (worker/auth.js), the
// same way normalizeRiddle.js is shared. No DOM, no crypto, no network:
// just the format rules and the username<->synthetic-email mapping, in
// one place so the client and server can never disagree about what a
// valid username is or which email a username maps to.
//
// Identity model (see docs/auth-design.md): the player picks a username
// and password, never an email. Supabase Auth is email-first, so each
// username is mapped to a deterministic synthetic address the player
// never sees. Because it's deterministic, login needs no server lookup —
// the browser rebuilds the same address from the typed username.

/** The domain half of the synthetic address. Not a real mail domain and
 * never receives mail — Supabase's "Confirm email" is off. */
export const SYNTHETIC_EMAIL_DOMAIN = "users.habitathero.app";

export const USERNAME_MIN = 3;
export const USERNAME_MAX = 20;
export const PASSWORD_MIN = 8;
// bcrypt (what Supabase Auth uses) silently truncates at 72 bytes — treat
// anything longer as invalid rather than letting the tail be ignored.
export const PASSWORD_MAX = 72;

// Case-insensitive. Kept deliberately small — names that would be
// confusing or impersonating coming from the game itself.
const RESERVED_USERNAMES = new Set([
  "admin", "administrator", "root", "system", "sys", "moderator", "mod",
  "support", "help", "staff", "owner", "guest", "anonymous", "null",
  "habitathero", "habitat_hero", "official"
]);

/** Lowercased, for comparison + the synthetic email. Usernames are
 * stored case-insensitively (citext) so "Fox" and "fox" are the same
 * account; this is the canonical form. */
export function normalizeUsername(raw) {
  return String(raw ?? "").trim().toLowerCase();
}

/** The address Supabase Auth actually stores for a username. Deterministic
 * on purpose: login = signInWithPassword({ email: syntheticEmail(name) }),
 * no "look up the email for this username" round trip. */
export function syntheticEmail(username) {
  return `${normalizeUsername(username)}@${SYNTHETIC_EMAIL_DOMAIN}`;
}

/**
 * @param {string} raw - the username as typed
 * @returns {{ ok: true, value: string } | { ok: false, error: string }}
 *   `value` is the normalized form; `error` is a short player-facing message.
 */
export function validateUsername(raw) {
  const value = normalizeUsername(raw);
  if (value.length < USERNAME_MIN || value.length > USERNAME_MAX) {
    return { ok: false, error: `Username must be ${USERNAME_MIN}–${USERNAME_MAX} characters.` };
  }
  if (!/^[a-z0-9_]+$/.test(value)) {
    return { ok: false, error: "Username can use letters, numbers, and underscore only." };
  }
  if (RESERVED_USERNAMES.has(value)) {
    return { ok: false, error: "That username isn't available." };
  }
  return { ok: true, value };
}

/**
 * @param {string} raw - the password as typed (never trimmed — spaces are
 *   valid password characters)
 * @returns {{ ok: true } | { ok: false, error: string }}
 */
export function validatePassword(raw) {
  const pw = String(raw ?? "");
  if (pw.length < PASSWORD_MIN) {
    return { ok: false, error: `Password must be at least ${PASSWORD_MIN} characters.` };
  }
  if (pw.length > PASSWORD_MAX) {
    return { ok: false, error: `Password must be at most ${PASSWORD_MAX} characters.` };
  }
  return { ok: true };
}

// ---- Recovery code -------------------------------------------------------
// 20 Crockford-base32 chars (no I/L/O/U — unambiguous when written by
// hand), shown grouped as XXXXX-XXXXX-XXXXX-XXXXX. ~100 bits of entropy,
// so the server can store a fast salted SHA-256 hash rather than a slow
// password hash. Generation needs a CSPRNG and lives in systems/auth.js
// (browser) / worker/auth.js; only the shape check is shared here.

// ---- email (parents and teachers sign in with one; docs/parent-design.md) ----

export const EMAIL_MAX = 254;

/** Trimmed and lowercased: one address, one account, however it's typed. */
export function normalizeEmail(raw) {
  return String(raw ?? "").trim().toLowerCase();
}

/** True for something that's plausibly an email address and not one of
 * the game's synthetic username addresses. Deliverability isn't checked:
 * email sending is on hold. */
export function validateEmail(raw) {
  const value = normalizeEmail(raw);
  if (!value || value.length > EMAIL_MAX || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
    return { ok: false, error: "Enter a valid email address." };
  }
  if (value.endsWith(`@${SYNTHETIC_EMAIL_DOMAIN}`)) return { ok: false, error: "Enter a valid email address." };
  return { ok: true, value };
}

/** Sign-in and reset forms take either: an "@" means an email. */
export function isEmailIdentifier(raw) {
  return String(raw ?? "").includes("@");
}

/** The address an account signs in with, from what was typed. */
export function signInEmail(identifier) {
  return isEmailIdentifier(identifier) ? normalizeEmail(identifier) : syntheticEmail(identifier);
}

/** True for an auth user still on a username (synthetic) address. */
export function isSyntheticEmail(email) {
  return String(email ?? "").toLowerCase().endsWith(`@${SYNTHETIC_EMAIL_DOMAIN}`);
}

export const RECOVERY_ALPHABET = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";
export const RECOVERY_LENGTH = 20;

/** Strips spaces/dashes and upper-cases — accept a code however the
 * player typed it back in. Returns the bare alphabet-only string. */
export function normalizeRecoveryCode(raw) {
  return String(raw ?? "").toUpperCase().replace(/[^0-9A-Z]/g, "");
}

/** Display form: XXXXX-XXXXX-XXXXX-XXXXX */
export function formatRecoveryCode(bare) {
  return normalizeRecoveryCode(bare).replace(/(.{5})(?=.)/g, "$1-");
}

export function isValidRecoveryShape(raw) {
  const bare = normalizeRecoveryCode(raw);
  return bare.length === RECOVERY_LENGTH && [...bare].every((c) => RECOVERY_ALPHABET.includes(c));
}

// ---- Access code ----------------------------------------------------------
// A required gate on ACCOUNT CREATION only (never login, never reset) — see
// docs/auth-design.md and docs/decisionlog.md. Same alphabet and "strip
// separators, uppercase" normalize rule as the recovery code (so the two
// look and feel like one family to a player typing either), but shorter:
// it only has to survive being guessed against a rate-limited, lockout-able
// endpoint, not defend an existing account, and a shorter code is easier
// for a child to type correctly from a printed slip or a parent's email.
// Kept as distinctly-named exports (not reuses of the recovery-code
// constants) so a call site reads "this is an access code" without having
// to check a length constant to know which one it's looking at.
//
// Where a code actually comes from (typed by an admin, generated for a
// promo, minted after a future payment) is invisible here on purpose — see
// worker/accessCodes.js's `source` field. This file only knows the shape.

export const ACCESS_CODE_ALPHABET = RECOVERY_ALPHABET;
export const ACCESS_CODE_LENGTH = 10;

/** Strips spaces/dashes and upper-cases — identical rule to the recovery
 * code, just named for what it's validating at each call site. */
export function normalizeAccessCode(raw) {
  return normalizeRecoveryCode(raw);
}

/** Display form: XXXXX-XXXXX */
export function formatAccessCode(bare) {
  return normalizeAccessCode(bare).replace(/(.{5})(?=.)/g, "$1-");
}

export function isValidAccessCodeShape(raw) {
  const bare = normalizeAccessCode(raw);
  return bare.length === ACCESS_CODE_LENGTH && [...bare].every((c) => ACCESS_CODE_ALPHABET.includes(c));
}
